import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  TextareaAutosize,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PageTitle from "../../../common/PageTitle";
import Loader from "../../../common/Loader";
import { getAPI, postAPI, uploadAPI } from "../../../network";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validation } from "../../../utils";

const useStyles = makeStyles(() => ({
  buttonRoot: {
    width: "100px",
    borderColor: "#707070",
    color: "#202E43",
    borderRadius: "8px",
    fontSize: "16px",
    textTransform: "none",
    marginRight: "15px",
    "&:hover": {
      backgroundColor: "#42505C",
      color: "white",
    },
  },
}));

const ANSWER_OPTIONS = [
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
  { value: "NA", label: "N/A" },
];

export default function SiteFormPage() {
  const { siteId, repId, inspId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();

  const [loader, setLoader] = useState(false);
  const [forms, setForms] = useState([]);
  const [answers, setAnswers] = useState({});
  const [mediaUrls, setMediaUrls] = useState({});
  const [notes, setNotes] = useState("");
  const [notesImage, setNotesImage] = useState("");
  const [notesImageUrl, setNotesImageUrl] = useState("");

  useEffect(() => {
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    setLoader(true);
    const data = await getAPI(`/report-questions?inspectionId=${inspId}`);
    if (data) setForms(data);
    setLoader(false);
  };

  const handleAnswerChange = (questionId, field, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value,
      },
    }));
  };

  const handleMediaChange = async (event, questionId) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }

    setLoader(true);
    const imageUrl = await uploadAPI(file);
    setLoader(false);

    setMediaUrls((prev) => ({
      ...prev,
      [questionId]: imageUrl,
    }));
  };

  const handleNotesImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select a valid image.");
      return;
    }

    setLoader(true);
    const uploadedUrl = await uploadAPI(file);
    setNotesImageUrl(uploadedUrl);
    setLoader(false);
  };

  const allQuestionsAnswered = forms.every(
    (q) => answers[q.id]?.answer && answers[q.id]?.comment
  );
  const handleSubmit = async () => {
    if (!allQuestionsAnswered) {
      toast.error("Please answer all questions before submitting.");
      return;
    }

    const payload = {
      reportTypeId: repId,
      siteId: siteId,
      questions: forms.map((q) => ({
        questionId: q.id,
        answer: answers[q.id]?.answer,
        comment: answers[q.id]?.comment,
        media: mediaUrls[q.id] || "",
      })),
      note: notes,
      media: notesImageUrl || "",
    };

    setLoader(true);
    const result = await postAPI("/reports", payload);
    setLoader(false);

    if (result) {
      navigate(-1);
    }
  };

  return (
    <Box sx={{ height: "inherit", px: 3 }}>
      <Loader loader={loader} />
      <PageTitle
        title="Site View"
        subTitle="Submit Form"
        headerTitle="Site Inspection"
      />

      {forms.length > 0 ? (
        <Box display="flex" flexDirection="column" rowGap={4} mb="20%">
          {forms.map((item) => (
            <Paper elevation={3} key={item.id} sx={{ p: 3 }}>
              <Typography fontWeight="bold" mb={1}>
                {item.question}
              </Typography>
              <br />
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Select Answer</InputLabel>
                  <Select
                    value={answers[item.id]?.answer || ""}
                    label="Select Answer"
                    onChange={(e) =>
                      handleAnswerChange(item.id, "answer", e.target.value)
                    }
                  >
                    {ANSWER_OPTIONS.map((opt) => (
                      <MenuItem value={opt.value} key={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextareaAutosize
                  placeholder="Comment"
                  minRows={3}
                  value={answers[item.id]?.comment || ""}
                  onChange={(e) =>
                    handleAnswerChange(item.id, "comment", e.target.value)
                  }
                  style={{
                    width: "30%",
                    fontSize: 16,
                    padding: 10,
                    borderRadius: 5,
                    border: "1px solid #ccc",
                    fontFamily: "Poppins, Helvetica, Arial",
                  }}
                />

                <Button
                  variant="outlined"
                  component="label"
                  sx={{
                    alignSelf: "center",
                    height: "40px",
                  }}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleMediaChange(e, item.id)}
                  />
                </Button>

                {mediaUrls[item.id] && (
                  <Typography alignSelf="center" fontSize={12} color="green">
                    Image uploaded
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}

          {/* Overall Notes Section */}
          {allQuestionsAnswered && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" mb={2}>
                Overall Notes
              </Typography>
              <TextareaAutosize
                placeholder="Write your final notes here..."
                minRows={4}
                style={{
                  width: "100%",
                  fontSize: 16,
                  padding: 10,
                  borderRadius: 5,
                  border: "1px solid #ccc",
                  fontFamily: "Poppins, Helvetica, Arial",
                }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <Box mt={2}>
                <Button variant="outlined" component="label">
                  Upload Notes Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleNotesImageUpload}
                  />
                </Button>
                {notesImageUrl && (
                  <Typography mt={1} fontSize={12} color="green">
                    Notes image uploaded
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          {/* Submit Controls */}
          <FormControl
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              mt: 4,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              className={classes.buttonRoot}
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              className={classes.buttonRoot}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </FormControl>
        </Box>
      ) : (
        <Typography textAlign="center" mt={4}>
          <h3>No Questions Added</h3>
        </Typography>
      )}
    </Box>
  );
}
